import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import audioBuzzAPI from '../../services/audioBuzzApi';

const WavToTxtConverter = ({ onConversionComplete, onError }) => {
  const [isConverting, setIsConverting] = useState(false);
  const [convertedFiles, setConvertedFiles] = useState([]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const wavFiles = acceptedFiles.filter(file => 
      file.name.toLowerCase().endsWith('.wav')
    );

    if (wavFiles.length === 0) {
      onError('Please upload WAV files only.');
      return;
    }

    setIsConverting(true);
    const results = [];

    try {
      for (const file of wavFiles) {
        try {
          const txtContent = await audioBuzzAPI.convertWavToTxt(file);
          const txtFileName = file.name.replace('.wav', '.txt');
          
          // Create blob URL for download
          const blob = new Blob([txtContent], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          
          results.push({
            originalName: file.name,
            txtName: txtFileName,
            downloadUrl: url,
            success: true
          });
        } catch (error) {
          results.push({
            originalName: file.name,
            success: false,
            error: error.message
          });
        }
      }

      setConvertedFiles(results);
      if (onConversionComplete) {
        onConversionComplete(results);
      }
    } catch (error) {
      onError(error.message);
    } finally {
      setIsConverting(false);
    }
  }, [onConversionComplete, onError]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/wav': ['.wav']
    },
    multiple: true
  });

  const downloadFile = (result) => {
    const a = document.createElement('a');
    a.href = result.downloadUrl;
    a.download = result.txtName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    convertedFiles.filter(f => f.success).forEach(file => {
      setTimeout(() => downloadFile(file), 100);
    });
  };

  const clearResults = () => {
    // Clean up blob URLs
    convertedFiles.forEach(file => {
      if (file.downloadUrl) {
        URL.revokeObjectURL(file.downloadUrl);
      }
    });
    setConvertedFiles([]);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">🔄 WAV to TXT Converter</h2>
        <p className="text-gray-400">Convert WAV audio files to frequency spectrum TXT format</p>
      </div>

      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive 
            ? 'border-blue-500 bg-gray-800' 
            : 'border-gray-700 hover:border-gray-600 hover:bg-gray-900'
        } ${isConverting ? 'pointer-events-none opacity-75' : ''}`}
      >
        <input {...getInputProps()} />
        {isConverting ? (
          <div className="space-y-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-gray-300 font-medium">Converting WAV files to TXT format...</p>
          </div>
        ) : isDragActive ? (
          <div className="space-y-2">
            <div className="text-4xl">🎵</div>
            <p className="text-blue-400 font-medium">Drop the WAV files here...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-5xl">🎵</div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-white">
                Drag & drop WAV files here, or click to select
              </p>
              <p className="text-sm text-gray-400">
                Supported: .wav files only
              </p>
              <p className="text-xs text-gray-500">
                Files will be converted using Welch method with 4096 FFT size
              </p>
            </div>
          </div>
        )}
      </div>

      {convertedFiles.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-white">Conversion Results ({convertedFiles.length})</h3>
            <div className="flex gap-3">
              <button 
                className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                  convertedFiles.filter(f => f.success).length === 0
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
                onClick={downloadAll}
                disabled={convertedFiles.filter(f => f.success).length === 0}
              >
                📥 Download All TXT Files
              </button>
              <button 
                className="px-4 py-2 text-red-400 bg-red-900/30 hover:bg-red-900/50 rounded-lg font-medium transition-colors duration-200 border border-red-800"
                onClick={clearResults}
              >
                🗑️ Clear Results
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-900/30 rounded-lg p-4 text-center border border-green-800">
              <div className="text-2xl font-bold text-green-400">
                {convertedFiles.filter(f => f.success).length}
              </div>
              <div className="text-sm text-green-300">Successful</div>
            </div>
            <div className="bg-red-900/30 rounded-lg p-4 text-center border border-red-800">
              <div className="text-2xl font-bold text-red-400">
                {convertedFiles.filter(f => !f.success).length}
              </div>
              <div className="text-sm text-red-300">Failed</div>
            </div>
          </div>

          <div className="space-y-3">
            {convertedFiles.map((result, index) => (
              <div key={index} className={`p-4 rounded-lg border ${
                result.success 
                  ? 'bg-green-900/20 border-green-800' 
                  : 'bg-red-900/20 border-red-800'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="font-medium text-white">{result.originalName}</span>
                      <span className="text-gray-500">→</span>
                      <span className={result.success ? 'text-green-400' : 'text-red-400'}>
                        {result.txtName || 'Failed'}
                      </span>
                    </div>
                    {result.error && (
                      <div className="mt-2 text-sm text-red-400">
                        Error: {result.error}
                      </div>
                    )}
                  </div>
                  {result.success && (
                    <button 
                      className="ml-4 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors duration-200"
                      onClick={() => downloadFile(result)}
                    >
                      📥 Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WavToTxtConverter;
